import React, { useState } from 'react';
import { MdSave, MdCheckCircle, MdPerson, MdAccountBalance, MdSchool, MdPhone, MdEmail, MdHome, MdInfo, MdCameraAlt, MdRefresh, MdLocationOn, MdCancel, MdSecurity } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import Webcam from 'react-webcam';
import api from '../api/axios';

const INITIAL_FORM_STATE = {
    applicant_name: '',
    father_husband_name: '',
    gender: '',
    dob: '',
    residential_address: '',
    category: '',
    aadhar_number: '',
    eid_number: '',
    bank_name: '',
    bank_branch: '',
    account_number: '',
    ifsc_code: '',
    mobile_number: '',
    email_id: '',
    inst_name: '',
    board_name: '',
    roll_no: '',
    passing_year: new Date().getFullYear(),
    student_photo: null,
};

const KushalYuvaForm = () => {
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState('');
    const [status, setStatus] = useState('');
    const [facingMode, setFacingMode] = useState("user");

    const [showCamera, setShowCamera] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [registeredBy, setRegisteredBy] = useState('');
    const [lastSubmission, setLastSubmission] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const [locError, setLocError] = useState(null);
    const webcamRef = React.useRef(null);

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);

    const capture = () => {
        const imageSrc = webcamRef.current.getScreenshot();
        setFormData(prev => ({ ...prev, student_photo: imageSrc }));
        setShowCamera(false);
    };

    const toggleCamera = () => {
        setFacingMode(prev => prev === "user" ? "environment" : "user");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirm(true);
        captureLocation();
    };

    const captureLocation = () => {
        setLocError(null);
        if (!navigator.geolocation) {
            setLocError("Location not supported by browser");
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            },
            (err) => {
                setLocError("Location permission denied. Please allow location to proceed.");
            }
        );
    };

    const confirmSubmit = async () => {
        if (!registeredBy.trim()) {
            alert("Please enter who is performing this registration.");
            return;
        }
        setLoading(true);
        setMsg('');
        setStatus('');

        try {
            const finalData = {
                ...formData,
                latitude: userLocation?.lat,
                longitude: userLocation?.lng,
                registered_by_name: registeredBy
            };
            const res = await api.post('/kushal-yuva/forms', finalData);

            // Save for success popup
            setLastSubmission({
                student: formData.applicant_name,
                by: registeredBy
            });

            setMsg(res.data.message || 'Form submitted successfully!');
            setStatus('success');
            setShowConfirm(false);
            setShowSuccess(true);

            // Reset form
            setFormData(INITIAL_FORM_STATE);
            setRegisteredBy('');
            setUserLocation(null);
            setLocError(null);
        } catch (error) {
            console.error(error);
            setMsg(error.response?.data?.message || 'Failed to submit form. Please try again.');
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = "w-full px-3 py-2 border border-blue-100 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none transition-all bg-white text-gray-800 text-sm";
    const labelStyle = "block text-[11px] md:text-xs font-bold text-blue-900 mb-1 leading-tight";
    const sectionTitleStyle = "flex items-center gap-2 text-sm md:text-lg font-bold text-blue-900 mb-4 border-b pb-2 border-blue-100 uppercase tracking-tight";

    return (
        <div className="max-w-4xl mx-auto py-4 px-2">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-50"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-700 to-indigo-800 p-6 text-white text-center relative">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                        <MdSchool size={100} />
                    </div>
                    <h1 className="text-2xl font-bold">कुशल युवा कार्यक्रम (KYP)</h1>
                    <p className="text-blue-100 text-sm mt-1">प्रशिक्षण निबंधन फॉर्म (Training Registration Form)</p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-8">
                    {msg && (
                        <div className={`p-4 rounded-lg flex items-center gap-3 ${status === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {status === 'success' ? <MdCheckCircle size={20} /> : <MdInfo size={20} />}
                            <span className="font-medium">{msg}</span>
                        </div>
                    )}

                    {/* Section 1: Basic Details */}
                    <section>
                        <h2 className={sectionTitleStyle}>
                            <MdPerson className="text-blue-600" /> व्यक्तिगत विवरणी (Personal Details)
                        </h2>

                        {/* Photo Capture Section */}
                        <div className="mb-6 flex flex-col items-center justify-center p-4 bg-blue-50/30 rounded-2xl border-2 border-dashed border-blue-100">
                            {formData.student_photo ? (
                                <div className="relative group">
                                    <img src={formData.student_photo} alt="Student" className="w-40 h-48 object-cover rounded-xl shadow-md border-2 border-white" />
                                    <button
                                        type="button"
                                        onClick={() => setShowCamera(true)}
                                        className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                                        title="Retake Photo"
                                    >
                                        <MdRefresh />
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2 text-blue-400">
                                        <MdCameraAlt size={40} />
                                    </div>
                                    <p className="text-xs text-blue-900 font-bold mb-3">आवेदक का लाइव फोटो (Live Photo Required)</p>
                                    <button
                                        type="button"
                                        onClick={() => setShowCamera(true)}
                                        className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 transition-all"
                                    >
                                        कैमरा खोलें (Open Camera)
                                    </button>
                                </div>
                            )}

                            {/* Camera Modal/Overlay */}
                            <AnimatePresence>
                                {showCamera && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4"
                                    >
                                        <div className="relative bg-white p-2 rounded-2xl overflow-hidden max-w-sm w-full shadow-2xl">
                                            <Webcam
                                                audio={false}
                                                ref={webcamRef}
                                                screenshotFormat="image/jpeg"
                                                className="w-full rounded-xl"
                                                videoConstraints={{
                                                    facingMode: facingMode
                                                }}
                                            />
                                            <div className="flex gap-4 p-4 items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowCamera(false)}
                                                    className="px-4 py-2 text-gray-400 font-bold text-xs"
                                                >
                                                    Cancel
                                                </button>

                                                <div className="flex-1 flex justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={capture}
                                                        className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                                                    >
                                                        <MdCameraAlt size={24} />
                                                    </button>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={toggleCamera}
                                                    className="p-3 text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
                                                    title="Switch Camera"
                                                >
                                                    <MdRefresh size={20} className={facingMode === 'environment' ? 'rotate-180 transition-all' : 'transition-all'} />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelStyle}>1. आवेदक का नाम (Applicant Name)</label>
                                <input name="applicant_name" value={formData.applicant_name} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>2. पिता / पति का नाम (Father/Husband Name)</label>
                                <input name="father_husband_name" value={formData.father_husband_name} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>3. लिंग (Gender)</label>
                                <select name="gender" value={formData.gender} onChange={handleChange} className={inputStyle} required>
                                    <option value="">Select Gender</option>
                                    <option value="male">पुरुष (Male)</option>
                                    <option value="female">महिला (Female)</option>
                                    <option value="transgender">तृतीय (Transgender)</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelStyle}>4. जन्म तिथि (Date of Birth)</label>
                                <input type="date" name="dob" value={formData.dob} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelStyle}>5. आवासीय पता (Residential Address)</label>
                                <textarea name="residential_address" value={formData.residential_address} onChange={handleChange} className={`${inputStyle} h-20 resize-none`} required />
                            </div>
                            <div>
                                <label className={labelStyle}>6. कोटि (Category)</label>
                                <select name="category" value={formData.category} onChange={handleChange} className={inputStyle} required>
                                    <option value="">Select Category</option>
                                    <option value="SC">(1) अनुसूचित जाति (SC)</option>
                                    <option value="ST">(2) अनुसूचित जनजाति (ST)</option>
                                    <option value="EBC">(3) अति पिछड़ा वर्ग (EBC)</option>
                                    <option value="BC">(4) पिछड़ा वर्ग (BC)</option>
                                    <option value="General">(5) सामान्य (General)</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelStyle}>7. आधार कार्ड संख्या (Aadhar Card Number)</label>
                                <input name="aadhar_number" value={formData.aadhar_number} onChange={handleChange} className={inputStyle} placeholder="12 digit aadhar" />
                                <p className="text-[10px] text-gray-500 mt-1 italic">यदि आधार कार्ड नहीं मिला है तो उसकी पंजीयन संख्या (EID NO.)</p>
                                <input name="eid_number" value={formData.eid_number} onChange={handleChange} className={`${inputStyle} mt-1`} placeholder="EID Number (if no Aadhar)" />
                            </div>
                        </div>
                    </section>

                    {/* Section 2: Contact & Bank */}
                    <section>
                        <h2 className={sectionTitleStyle}>
                            <MdAccountBalance className="text-blue-600" /> बैंक एवं संपर्क विवरणी (Bank & Contact Details)
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelStyle}>8(1). बैंक का नाम (Bank Name)</label>
                                <input name="bank_name" value={formData.bank_name} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>8(2). शाखा का नाम (Branch Name)</label>
                                <input name="bank_branch" value={formData.bank_branch} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>8(3). खाता संख्या (Account Number)</label>
                                <input name="account_number" value={formData.account_number} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>8(4). IFSC कोड (IFSC Code)</label>
                                <input name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div>
                                <label className={labelStyle}>9. मोबाइल न० (Mobile No.)</label>
                                <input name="mobile_number" value={formData.mobile_number} onChange={handleChange} className={inputStyle} required />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelStyle}>10. ईमेल आई० डी० (Email ID)</label>
                                <input type="email" name="email_id" value={formData.email_id} onChange={handleChange} className={inputStyle} required />
                            </div>
                        </div>
                    </section>

                    {/* Section 3: Education */}
                    <section>
                        <h2 className={sectionTitleStyle}>
                            <MdSchool className="text-blue-600" /> शैक्षणिक योग्यता (Educational Qualification)
                        </h2>
                        <div className="overflow-x-auto border border-blue-100 rounded-lg -mx-4 md:mx-0">
                            <table className="w-full text-xs md:text-sm text-left min-w-[700px]">
                                <thead className="bg-blue-50 text-blue-900 font-bold">
                                    <tr>
                                        <th className="px-4 py-3 border-r border-blue-100 italic whitespace-nowrap">परीक्षा उत्तीर्ण</th>
                                        <th className="px-4 py-3 border-r border-blue-100 whitespace-nowrap">विद्यालय / संस्थान का नाम</th>
                                        <th className="px-4 py-3 border-r border-blue-100 whitespace-nowrap">बोर्ड / परिषद का नाम</th>
                                        <th className="px-4 py-3 border-r border-blue-100 whitespace-nowrap">अनुक्रमांक</th>
                                        <th className="px-4 py-3 whitespace-nowrap">वर्ष</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-t border-blue-100 italic">
                                        <td className="px-4 py-4 bg-gray-50 font-medium border-r border-blue-100 whitespace-nowrap">इंटरमीडिएट (12वीं)</td>
                                        <td className="p-0 border-r border-blue-100">
                                            <input name="inst_name" value={formData.inst_name} onChange={handleChange} className="w-full p-4 border-0 focus:ring-1 focus:ring-blue-400 bg-transparent outline-none" required />
                                        </td>
                                        <td className="p-0 border-r border-blue-100">
                                            <input name="board_name" value={formData.board_name} onChange={handleChange} className="w-full p-4 border-0 focus:ring-1 focus:ring-blue-400 bg-transparent outline-none" required />
                                        </td>
                                        <td className="p-0 border-r border-blue-100">
                                            <input name="roll_no" value={formData.roll_no} onChange={handleChange} className="w-full p-4 border-0 focus:ring-1 focus:ring-blue-400 bg-transparent outline-none" required />
                                        </td>
                                        <td className="p-0">
                                            <input type="number" name="passing_year" value={formData.passing_year} onChange={handleChange} className="w-full p-4 border-0 focus:ring-1 focus:ring-blue-400 bg-transparent outline-none" required />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Submit */}
                    <div className="pt-6 border-t">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transform active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                            ) : (
                                <><MdSave size={20} /> फॉर्म सुरक्षित करें (Save Form)</>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>

            {/* Final Confirmation Modal */}
            <AnimatePresence>
                {showConfirm && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-blue-950/80 backdrop-blur-md">
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 50, opacity: 0 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-2xl max-w-sm w-full"
                        >
                            <div className="bg-gradient-to-r from-blue-700 to-indigo-800 p-5 text-center text-white">
                                <MdSecurity size={40} className="mx-auto mb-2 opacity-90" />
                                <h3 className="text-lg font-bold">Secure Registration</h3>
                                <p className="text-[10px] text-white/70 uppercase tracking-widest">Verification Required</p>
                            </div>

                            <div className="p-5 space-y-4">
                                <div className="space-y-3">
                                    <div className="p-3 bg-blue-50 rounded-2xl border border-blue-100">
                                        <label className="text-[10px] text-blue-900 uppercase font-black tracking-widest block mb-1">
                                            This registration is being performed by:
                                        </label>
                                        <div className="relative">
                                            <MdPerson className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" />
                                            <input
                                                type="text"
                                                value={registeredBy}
                                                onChange={(e) => setRegisteredBy(e.target.value)}
                                                placeholder="Enter Staff/Teacher Name..."
                                                className="w-full pl-9 pr-3 py-2 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold text-gray-800"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl border border-gray-100 relative">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${userLocation ? 'bg-green-100 text-green-600' : 'bg-red-50 text-red-500'}`}>
                                            <MdLocationOn size={20} className={!userLocation && !locError ? 'animate-bounce' : ''} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-[9px] text-gray-500 uppercase font-black tracking-widest">Live Location Status</p>
                                            {userLocation ? (
                                                <p className="text-[11px] font-bold text-green-700">✓ Location Verified</p>
                                            ) : (
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-[11px] font-bold text-red-500 leading-tight">{locError || 'Fetching GPS...'}</p>
                                                    {locError && (
                                                        <button
                                                            type="button"
                                                            onClick={captureLocation}
                                                            className="text-[9px] bg-red-100 text-red-600 px-2 py-1 rounded-md font-bold uppercase shrink-0"
                                                        >
                                                            Retry
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 pt-1">
                                    <button
                                        onClick={confirmSubmit}
                                        disabled={loading || !userLocation || !registeredBy.trim()}
                                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:bg-gray-200 disabled:shadow-none transition-all flex items-center justify-center gap-2 text-sm"
                                    >
                                        {loading ? (
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>Confirm & Submit</>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setShowConfirm(false)}
                                        disabled={loading}
                                        className="w-full py-1 text-xs text-gray-400 font-bold hover:text-gray-600"
                                    >
                                        Go Back & Edit
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccess && lastSubmission && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-blue-950/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl border-t-8 border-green-500"
                        >
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                <MdCheckCircle size={40} />
                            </div>
                            <h3 className="text-xl font-black text-gray-900 mb-1">Registration Done!</h3>
                            <p className="text-gray-500 text-[10px] mb-4 uppercase tracking-widest font-bold">Secure ID: #{Math.floor(Math.random() * 900000 + 100000)}</p>

                            <div className="bg-gray-50 rounded-2xl p-4 mb-5 space-y-3 relative overflow-hidden">
                                {formData.student_photo && (
                                    <img src={formData.student_photo} alt="Student" className="w-12 h-12 rounded-full absolute -top-1 -right-1 opacity-20 filter grayscale" />
                                )}
                                <div className="text-left">
                                    <p className="text-[9px] text-gray-400 uppercase font-black tracking-widest leading-none mb-1">Applicant Name</p>
                                    <p className="text-base font-black text-blue-900">{lastSubmission.student}</p>
                                </div>
                                <div className="h-px bg-gray-200 w-full" />
                                <div className="text-left">
                                    <p className="text-[9px] text-gray-400 uppercase font-black tracking-widest leading-none mb-1">Registered By</p>
                                    <p className="text-sm font-bold text-green-700 capitalize">Mr. {lastSubmission.by}</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setShowSuccess(false)}
                                className="w-full bg-blue-900 text-white py-3 rounded-xl font-bold hover:bg-black transition-all shadow-lg"
                            >
                                Continue
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default KushalYuvaForm;
